// 加工指示明細の金額計算(ロス率・通貨・丸め規則)をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiDetailCalcService(deps) {
    const {
        DicVal,
        KAKOUSIJI_CURRENCY_INPUT_LOCK_BY_TYPE,
        KAKOUSIJI_DETAIL_CALC_CONFIG_BY_TYPE,
        KAKOUSIJI_DOMESTIC_CURRENCY_CODE,
        KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE,
        KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE,
        KAKOUSIJI_SOZAI_MEISAI_PANEL_TYPE,
        columninfo,
        columns,
        entrydataRef,
        isEmpty,
        kakousijiAmountRoundingRef,
        normalizeKakousijiText,
        normalizeKakousijiUpperColumnId
    } = deps;

    function normalizeKakousijiCalcText(value) {
        return String(value ?? '').replace(/[\s　]/g, '').trim();
    }
    function toKakousijiCalcNumber(value) {
        const raw = String(value ?? '').replace(/,/g, '').trim();
        if (raw === '') return 0;
        const num = Number(raw);
        return Number.isFinite(num) ? num : 0;
    }
    function roundKakousijiCalcNumber(value, decimalPlaces = 2, rounding = 1) {
        const num = toKakousijiCalcNumber(value);
        const dpRaw = Number(decimalPlaces ?? 2);
        const dp = Number.isFinite(dpRaw) ? Math.max(0, dpRaw) : 2;
        const scale = Math.pow(10, dp);
        const mode = String(rounding ?? '1').trim();
        if (mode === '0') return Math.floor(num * scale + 1e-9) / scale;
        if (mode === '2') return Math.ceil(num * scale - 1e-9) / scale;
        return Math.round((num + Number.EPSILON) * scale) / scale;
    }
    function formatKakousijiCalcNumber(value, decimalPlaces = 2, rounding = 1) {
        const dpRaw = Number(decimalPlaces ?? 2);
        const dp = Number.isFinite(dpRaw) ? Math.max(0, dpRaw) : 2;
        const rounded = roundKakousijiCalcNumber(value, dp, rounding);
        if (!Number.isFinite(rounded)) return '';
        return rounded.toFixed(dp).replace(/\.?0+$/, '').replace(/^-0$/, '0');
    }
    function resolveKakousijiDetailCalcDisplayColumnMeta(panelType, columnId) {
        const targetId = normalizeKakousijiUpperColumnId(columnId);
        if (targetId === '') return null;
        const sources = [
            ...(Array.isArray(columninfo) ? columninfo : []),
            ...(Array.isArray(columns) ? columns : [])
        ];
        const hit = sources.find((col) => {
            if (normalizeKakousijiText(DicVal(col, 'type', '')) !== normalizeKakousijiText(panelType)) return false;
            const candidate = normalizeKakousijiUpperColumnId(DicVal(col, 'columnid', DicVal(col, 'colId', DicVal(col, 'field', ''))));
            return candidate === targetId;
        });
        if (isEmpty(hit)) return null;
        return { columnId: targetId };
    }
    function resolveKakousijiAmountRoundingRule() {
        return normalizeKakousijiAmountRoundingRule(DicVal(kakousijiAmountRoundingRef.current, 'amount', null), 0, '1');
    }
    function resolveKakousijiForeignAmountRoundingRule(currencyCode) {
        const settings = kakousijiAmountRoundingRef.current;
        const fallback = normalizeKakousijiAmountRoundingRule(DicVal(settings, 'amountforeigndefault', null), 2, '1');
        const code = normalizeKakousijiCalcText(currencyCode).toUpperCase();
        if (code === '') return fallback;
        const map = DicVal(settings, 'amountforeign', null);
        if (isEmpty(map) || typeof map !== 'object') return fallback;
        const hitKey = Object.keys(map).find((key) => String(key ?? '').trim().toUpperCase() === code);
        if (isEmpty(hitKey)) return fallback;
        return normalizeKakousijiAmountRoundingRule(map[hitKey], fallback.decimalplace, fallback.rounding);
    }
    function normalizeKakousijiAmountRoundingRule(rule, defaultDecimalPlaces, defaultRounding) {
        const decimalRaw = Number(DicVal(rule, 'decimalplace', defaultDecimalPlaces));
        const roundingRaw = String(DicVal(rule, 'rounding', defaultRounding) ?? defaultRounding).trim();
        return {
            decimalplace: Number.isFinite(decimalRaw) ? Math.max(0, decimalRaw) : defaultDecimalPlaces,
            rounding: roundingRaw === '' ? defaultRounding : roundingRaw
        };
    }
    function resolveKakousijiDetailRowCurrency(row, currencyField) {
        const field = String(currencyField ?? '').trim();
        const rowValue = field === '' ? '' : normalizeKakousijiCalcText(DicVal(row, field, ''));
        if (rowValue !== '') return rowValue;
        return normalizeKakousijiCalcText(DicVal(entrydataRef.current, 'SSD_TSUKATANI', ''));
    }
    function isKakousijiDomesticCurrencyRow(row, currencyField) {
        const code = resolveKakousijiDetailRowCurrency(row, currencyField).toUpperCase();
        return code === '' || code === KAKOUSIJI_DOMESTIC_CURRENCY_CODE;
    }
    function resolveKakousijiCurrencyInputLockSide(config, columnId) {
        const cid = normalizeKakousijiUpperColumnId(columnId);
        if (cid === '' || isEmpty(config)) return '';
        if ((config.domesticFields ?? []).some((id) => normalizeKakousijiUpperColumnId(id) === cid)) return 'domestic';
        if ((config.foreignFields ?? []).some((id) => normalizeKakousijiUpperColumnId(id) === cid)) return 'foreign';
        return '';
    }
    function applyKakousijiCurrencyInputLockColDef(column) {
        if (isEmpty(column) || typeof column !== 'object') return;
        const panelType = normalizeKakousijiText(DicVal(DicVal(column, 'colinfo', {}), 'type', ''));
        const config = KAKOUSIJI_CURRENCY_INPUT_LOCK_BY_TYPE[panelType];
        if (isEmpty(config)) return;
        const columnId = normalizeKakousijiUpperColumnId(DicVal(column, 'colId', DicVal(column, 'field', '')));
        const side = resolveKakousijiCurrencyInputLockSide(config, columnId);
        if (side === '') return;
        const isCurrencyEnabled = (params) => {
            const domestic = isKakousijiDomesticCurrencyRow(DicVal(params, 'data', null), config.currencyField);
            return domestic ? side === 'domestic' : side === 'foreign';
        };
        const baseEditable = column.editable;
        column.editable = function (params) {
            const edit = typeof baseEditable === 'function' ? baseEditable(params) : baseEditable !== false;
            if (edit === false) return false;   // 元々編集不可のセルは緩めない
            return isCurrencyEnabled(params);
        };
        column.cellStyle = function (params) {
            return isCurrencyEnabled(params) ? { backgroundColor: 'inherit' } : { backgroundColor: '#f2f2f2' };
        };
    }
    function applyKakousijiCurrencyPriceRuleToRow(row, calcConfig = {}) {
        if (isEmpty(row) || typeof row !== 'object') return row;
        const unitField = String(DicVal(calcConfig, 'unitField', '') ?? '').trim();
        const unitForeignField = String(DicVal(calcConfig, 'unitForeignField', '') ?? '').trim();
        if (unitField === '' || unitForeignField === '') return row;
        const currencyField = String(DicVal(calcConfig, 'currencyField', '') ?? '').trim();
        if (isKakousijiDomesticCurrencyRow(row, currencyField)) {
            row[unitForeignField] = 0;
            return row;
        }
        const rateField = String(DicVal(calcConfig, 'rateField', '') ?? '').trim();
        const rate = rateField === '' ? 0 : toKakousijiCalcNumber(DicVal(row, rateField, ''));
        if (rate === 0) {
            row[unitField] = 0;
            return row;
        }
        const rule = resolveKakousijiDomesticPriceRoundingRule();
        const unitForeign = toKakousijiCalcNumber(DicVal(row, unitForeignField, ''));
        row[unitField] = roundKakousijiCalcNumber(unitForeign * rate, rule.decimalplace, rule.rounding);
        return row;
    }
    function resolveKakousijiDomesticPriceRoundingRule() {
        const map = DicVal(kakousijiAmountRoundingRef.current, 'amountforeign', null);
        if (isEmpty(map) || typeof map !== 'object') return { decimalplace: 0, rounding: '1' };
        const hitKey = Object.keys(map).find((key) => String(key ?? '').trim().toUpperCase() === KAKOUSIJI_DOMESTIC_CURRENCY_CODE);
        if (isEmpty(hitKey)) return { decimalplace: 0, rounding: '1' };
        return normalizeKakousijiAmountRoundingRule(map[hitKey], 0, '1');
    }
    function recalcKakousijiLossBasedDetailRow(row, calcConfig = {}) {
        if (isEmpty(row) || typeof row !== 'object') return row;
        const next = { ...row };
        // 単価の正規化は数量・ロス率の有無に関わらず行う。
        applyKakousijiCurrencyPriceRuleToRow(next, calcConfig);
        const qtyField = String(DicVal(calcConfig, 'qtyField', '') ?? '').trim();
        const lossField = String(DicVal(calcConfig, 'lossField', '') ?? '').trim();
        const outputField = String(DicVal(calcConfig, 'outputField', '') ?? '').trim();
        const unitField = String(DicVal(calcConfig, 'unitField', '') ?? '').trim();
        const unitForeignField = String(DicVal(calcConfig, 'unitForeignField', '') ?? '').trim();
        const outputDecimalPlaces = Number(DicVal(calcConfig, 'outputDecimalPlaces', 2));
        const outputRounding = String(DicVal(calcConfig, 'outputRounding', 1) ?? '1');
        const currencyField = String(DicVal(calcConfig, 'currencyField', '') ?? '').trim();
        const panelType = String(DicVal(calcConfig, 'panelType', '') ?? '').trim();
        const amountMeta = resolveKakousijiDetailCalcDisplayColumnMeta(panelType, DicVal(calcConfig, 'amountColumnId', ''));
        const amountForeignMeta = resolveKakousijiDetailCalcDisplayColumnMeta(panelType, DicVal(calcConfig, 'amountForeignColumnId', ''));
        const qtyRaw = DicVal(next, qtyField, '');
        const lossRaw = DicVal(next, lossField, '');
        const hasQty = normalizeKakousijiCalcText(qtyRaw) !== '';
        const hasLoss = normalizeKakousijiCalcText(lossRaw) !== '';
        if (!hasQty && !hasLoss) {
            if (outputField !== '') next[outputField] = '';
            if (!isEmpty(amountMeta)) next[amountMeta.columnId] = '';
            if (!isEmpty(amountForeignMeta)) next[amountForeignMeta.columnId] = '';
            return next;
        }
        const qty = toKakousijiCalcNumber(qtyRaw);
        const lossRate = toKakousijiCalcNumber(lossRaw);
        const unit = toKakousijiCalcNumber(DicVal(next, unitField, ''));
        const unitForeign = toKakousijiCalcNumber(DicVal(next, unitForeignField, ''));
        const calcQty = qty * (1 + (lossRate / 100));
        // 金額は画面に表示されるロス込要尺（丸め後）×単価とする。
        const lossQty = roundKakousijiCalcNumber(calcQty, outputDecimalPlaces, outputRounding);
        if (outputField !== '') next[outputField] = formatKakousijiCalcNumber(lossQty, outputDecimalPlaces, outputRounding);
        if (!isEmpty(amountMeta)) {
            const amountRule = resolveKakousijiAmountRoundingRule();
            next[amountMeta.columnId] = roundKakousijiCalcNumber(lossQty * unit, amountRule.decimalplace, amountRule.rounding);
        }
        if (!isEmpty(amountForeignMeta)) {
            const foreignRule = resolveKakousijiForeignAmountRoundingRule(resolveKakousijiDetailRowCurrency(next, currencyField));
            next[amountForeignMeta.columnId] = roundKakousijiCalcNumber(lossQty * unitForeign, foreignRule.decimalplace, foreignRule.rounding);
        }
        return next;
    }
    function recalcKakousijiMaterialDetailCalcRow(row) {
        return recalcKakousijiLossBasedDetailRow(row, KAKOUSIJI_DETAIL_CALC_CONFIG_BY_TYPE[KAKOUSIJI_SOZAI_MEISAI_PANEL_TYPE]);
    }
    function recalcKakousijiAccessoryDetailCalcRow(row) {
        return recalcKakousijiLossBasedDetailRow(row, KAKOUSIJI_DETAIL_CALC_CONFIG_BY_TYPE[KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE]);
    }
    function recalcKakousijiMaterialDetailCalcRows(rows) {
        const list = Array.isArray(rows) ? rows : [];
        if (list.length === 0) return list;
        return list.map((row) => recalcKakousijiMaterialDetailCalcRow(row));
    }
    function recalcKakousijiAccessoryDetailCalcRows(rows) {
        const list = Array.isArray(rows) ? rows : [];
        if (list.length === 0) return list;
        return list.map((row) => recalcKakousijiAccessoryDetailCalcRow(row));
    }
    function recalcKakousijiFukushizaiListCalcRows(rows) {
        const list = Array.isArray(rows) ? rows : [];
        if (list.length === 0) return list;
        return list.map((row) => recalcKakousijiFukushizaiListCalcRow(row));
    }
    function recalcKakousijiFukushizaiListCalcRow(row) {
        if (isEmpty(row) || typeof row !== 'object') return row;
        const next = { ...row };
        // 単価の正規化は数量・ロス率の有無に関わらず行う。
        applyKakousijiCurrencyPriceRuleToRow(next, KAKOUSIJI_DETAIL_CALC_CONFIG_BY_TYPE[KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE]);
        const qtyRaw = DicVal(next, 'SSDZM_YOJAKUKOSU', '');
        const lossRaw = DicVal(next, 'SSDZM_LOSSRITU', '');
        const hasQty = normalizeKakousijiCalcText(qtyRaw) !== '';
        const hasLoss = normalizeKakousijiCalcText(lossRaw) !== '';
        const amountMeta = resolveKakousijiDetailCalcDisplayColumnMeta(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE, 'FUKUSHIZAI_ICHIRAN_KINGAKU');
        const amountForeignMeta = resolveKakousijiDetailCalcDisplayColumnMeta(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE, 'FUKUSHIZAI_ICHIRAN_KINGAKU_GAIKA');
        if (!hasQty && !hasLoss) {
            next.SSDZM_LSURYO = '';
            if (!isEmpty(amountMeta)) next[amountMeta.columnId] = '';
            if (!isEmpty(amountForeignMeta)) next[amountForeignMeta.columnId] = '';
            return next;
        }
        const qty = toKakousijiCalcNumber(qtyRaw);
        const lossRate = toKakousijiCalcNumber(lossRaw);
        const unit = toKakousijiCalcNumber(DicVal(next, 'SSDZM_TANKA', ''));
        const unitForeign = toKakousijiCalcNumber(DicVal(next, 'SSDZM_TANKAF', ''));
        const calcQty = qty * (1 + (lossRate / 100));
        // 金額は画面に表示されるロス込数量（丸め後）×単価とする。
        const lossQty = roundKakousijiCalcNumber(calcQty, 2, 1);
        next.SSDZM_LSURYO = formatKakousijiCalcNumber(lossQty, 2, 1);
        if (!isEmpty(amountMeta)) {
            const amountRule = resolveKakousijiAmountRoundingRule();
            next[amountMeta.columnId] = roundKakousijiCalcNumber(lossQty * unit, amountRule.decimalplace, amountRule.rounding);
        }
        if (!isEmpty(amountForeignMeta)) {
            const foreignRule = resolveKakousijiForeignAmountRoundingRule(resolveKakousijiDetailRowCurrency(next, 'SSDZM_CUR'));
            next[amountForeignMeta.columnId] = roundKakousijiCalcNumber(lossQty * unitForeign, foreignRule.decimalplace, foreignRule.rounding);
        }
        return next;
    }

    return {
        normalizeKakousijiCalcText,
        toKakousijiCalcNumber,
        roundKakousijiCalcNumber,
        formatKakousijiCalcNumber,
        resolveKakousijiDetailCalcDisplayColumnMeta,
        resolveKakousijiAmountRoundingRule,
        resolveKakousijiForeignAmountRoundingRule,
        normalizeKakousijiAmountRoundingRule,
        resolveKakousijiDetailRowCurrency,
        isKakousijiDomesticCurrencyRow,
        resolveKakousijiCurrencyInputLockSide,
        applyKakousijiCurrencyInputLockColDef,
        applyKakousijiCurrencyPriceRuleToRow,
        resolveKakousijiDomesticPriceRoundingRule,
        recalcKakousijiLossBasedDetailRow,
        recalcKakousijiMaterialDetailCalcRow,
        recalcKakousijiAccessoryDetailCalcRow,
        recalcKakousijiMaterialDetailCalcRows,
        recalcKakousijiAccessoryDetailCalcRows,
        recalcKakousijiFukushizaiListCalcRows,
        recalcKakousijiFukushizaiListCalcRow
    };
}
